// Implements: F-UIX-019
/**
 * One organization's ceiling, read and written through the shared query cache.
 *
 * The read is keyed by organization id, so an answer for an organization the admin has since
 * left is cached under its own key and never shown for another (checklist 14). A save answers
 * with the stored ceiling, which replaces the cached one; it is never retried (`retry: false`
 * in the client and in the mutation).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";

import type { ApiError } from "../../../shared/api/ApiError";
import type { CeilingRequest, CeilingView } from "../../../types/ceiling";
import { fetchCeiling, saveCeiling } from "../api/ceilingApi";

export function ceilingKey(organizationId: string): readonly ["admin", "ceiling", string] {
  return ["admin", "ceiling", organizationId];
}

export function useCeiling(organizationId: string): UseQueryResult<CeilingView, ApiError> {
  return useQuery<CeilingView, ApiError>({
    queryKey: ceilingKey(organizationId),
    queryFn: ({ signal }) => fetchCeiling(organizationId, signal),
  });
}

export function useSaveCeiling(organizationId: string): UseMutationResult<CeilingView, ApiError, CeilingRequest> {
  const client = useQueryClient();
  return useMutation<CeilingView, ApiError, CeilingRequest>({
    mutationFn: (ceiling) => saveCeiling(organizationId, ceiling),
    retry: false,
    onSuccess: (stored) => {
      // Cached under the organization the save named, whichever one is on screen now.
      client.setQueryData(ceilingKey(organizationId), stored);
    },
  });
}
